/* eslint-disable no-unused-vars */
import { TNotation } from "./TYPENotations";

// LQC-2254 - bond reference data from GET /Securities/bonds. This is the searchable
// universe a user picks from when building a block from scratch: the security must
// already exist in our database, so the ISIN / security name are matched against
// this list rather than free-typed.

// Field keys as the endpoint returns them. Use these instead of string literals in
// search field lists, colDefs and lookups so a contract change breaks the build.
export enum BondSecurityField {
  isin = "isin",
  securityName = "securityName",
  issuer = "issuer",
  currency = "currency",
  maturityDate = "maturityDate",
  couponRate = "couponRate",
  bidPrice = "bidPrice",
  bidPriceDate = "bidPriceDate",
  askPrice = "askPrice",
  askPriceDate = "askPriceDate",
  midPrice = "midPrice",
  midPriceDate = "midPriceDate",
  notation = "notation",
  bidYield = "bidYield",
  midYield = "midYield",
  askYield = "askYield",
  settlementDays = "settlementDays",
  risk = "risk",
  contractAmount = "contractAmount",
}

// Prices and their dates are nullable - the payload returns null when no price has
// been published. Dates are ISO strings (maturityDate is date-only: 2062-01-26).
export interface IBondSecurity {
  [BondSecurityField.isin]: string;
  [BondSecurityField.securityName]: string;
  [BondSecurityField.issuer]: string;
  [BondSecurityField.currency]: string;
  [BondSecurityField.maturityDate]: string;
  [BondSecurityField.couponRate]: number | null;
  [BondSecurityField.bidPrice]: number | null;
  [BondSecurityField.bidPriceDate]: string | null;
  [BondSecurityField.askPrice]: number | null;
  [BondSecurityField.askPriceDate]: string | null;
  [BondSecurityField.midPrice]: number | null;
  [BondSecurityField.midPriceDate]: string | null;
  [BondSecurityField.notation]: TNotation | null;
  // LQC-2575 - yields are being added to sp_GetBondsWithPrices. Optional so the
  // Prices widget compiles and runs against the payload as it stands today.
  [BondSecurityField.bidYield]?: number | null;
  [BondSecurityField.midYield]?: number | null;
  [BondSecurityField.askYield]?: number | null;
  // LQC-2993 - T+N for this bond. Null or absent falls back to T+2.
  [BondSecurityField.settlementDays]?: number | null;
  // LQC-2945. dbo.KeyRatio "Risk"; drives the ticket's DV01 (LQC-2941).
  [BondSecurityField.risk]?: number | null;
  [BondSecurityField.contractAmount]?: number | null;
}

// GET /Securities/bonds
export interface IBondSecuritiesResponse {
  bonds: IBondSecurity[];
}

// What a user types to find a bond. Order is the ranking tiebreak: ISIN first (the
// unique identifier), then the readable name, then the issuer for a broader sweep.
// Everything else on the security is detail shown once a bond is narrowed down.
export const BOND_SECURITY_SEARCH_FIELDS: BondSecurityField[] = [
  BondSecurityField.isin,
  BondSecurityField.securityName,
  BondSecurityField.issuer,
];

// The field that identifies a bond to the back end.
export const BOND_SECURITY_KEY_FIELD = BondSecurityField.isin;
